/**
 * Calculate country visits from trip segments
 * A "visit" = continuous time in each country (regardless of cities/hotels)
 * For cruises, expands daily itinerary to count port visits
 */

import { api } from "../utils/api";

/**
 * Fetch cruise itinerary days for a cruise segment
 */
const fetchCruiseItinerary = async (segmentId) => {
  try {
    const response = await api.get(`/cruise-itinerary/${segmentId}`);
    return response.data;
  } catch (error) {
    return [];
  }
};
/**
 * Expand cruise segment into daily visits based on itinerary
 */
const expandCruiseSegment = (cruiseSegment, cruiseItinerary) => {
  const dailyVisits = [];
  const cruiseStartDate = new Date(cruiseSegment.start_datetime);
  
  for (const day of cruiseItinerary) {
    // Calculate the actual date for this day
    // day_number is 1-indexed (day 1, 2, 3...), so subtract 1 for date calculation
    const dayDate = new Date(cruiseStartDate);
    dayDate.setDate(dayDate.getDate() + (day.day_number - 1));
    const dayDateStr = dayDate.toISOString();
    
    // Determine the country for this day
    // IMPORTANT: Never fall back to cruise segment's country (embarkation country)
    // Each day must have its own country specified in the itinerary
    let country;
    if (day.at_sea) {
      country = "International Waters";
    } else if (day.country) {
      country = day.country;
    } else {
      // Port day without country specified - skip or warn
      continue; // Skip this day if no country specified for a port
    }
    
    // Create a pseudo-segment for each day
    const dailySegment = {
      id: `${cruiseSegment.id}_day_${day.day_number}`,
      type: "cruise_day",
      country: country,
      start_datetime: dayDateStr,
      end_datetime: dayDateStr,
      port_name: day.port_name,
      at_sea: day.at_sea,
      cruise_segment_id: cruiseSegment.id,
      day_number: day.day_number
    };
    
    dailyVisits.push(dailySegment);
  }
  
  return dailyVisits;
};

export const calculateCountryVisits = async (segments, travelerId = null, cruiseItineraryBySegment = null) => {
  if (!segments || segments.length === 0) {
    return [];
  }

  // Filter out cancelled and mistake segments first
  let filteredSegments = segments.filter(segment => 
    !segment.is_cancelled && 
    !segment.is_mistake && 
    segment.status !== 'cancelled'
  );

  // Then filter by traveler if specified
  if (travelerId) {
    filteredSegments = filteredSegments.filter(segment => 
      segment.traveler_ids && segment.traveler_ids.includes(travelerId)
    );
    
    if (filteredSegments.length === 0) {
      return [];
    }
  }

  if (filteredSegments.length === 0) {
    return [];
  }

  // Expand cruise segments into daily visits. When the caller passes a
  // pre-fetched `cruiseItineraryBySegment` map (the Dashboard already
  // gets this in the `/dashboard/trips` aggregate payload), we read
  // from it instead of issuing per-cruise API calls — this is the
  // difference between ~30 network round-trips and 0 on dashboard load.
  const expandedSegments = [];
  for (const segment of filteredSegments) {
    if (segment.type === "cruise") {
      const itinerary = cruiseItineraryBySegment
        ? (cruiseItineraryBySegment[segment.id] || [])
        : await fetchCruiseItinerary(segment.id);
      
      if (itinerary && itinerary.length > 0) {
        const dailyVisits = expandCruiseSegment(segment, itinerary);
        expandedSegments.push(...dailyVisits);
      } else {
        expandedSegments.push(segment);
      }
    } else {
      expandedSegments.push(segment);
    }
  }

  // Sort all segments (including expanded cruise days) by date
  const sortedSegments = expandedSegments.sort((a, b) => {
    const dateA = new Date(a.start_datetime || a.date);
    const dateB = new Date(b.start_datetime || b.date);
    return dateA - dateB;
  });

  const visits = [];
  let currentVisit = null;

  sortedSegments.forEach((segment, index) => {
    const country = segment.country;
    
    // Skip segments without country data
    if (!country) return;

    // If this is the same country as current visit, extend it
    if (currentVisit && currentVisit.country === country) {
      currentVisit.segments.push(segment);
      
      // Track travelers for this visit (union of all segment travelers)
      if (segment.traveler_ids) {
        segment.traveler_ids.forEach(tid => currentVisit.travelerIds.add(tid));
      }
      
      // For transport segments (flight/train/bus) leaving this country,
      // the visit ends when the transport STARTS (departure), not when it ends (arrival)
      // For accommodation/activities, the visit extends to the end of the segment
      if (segment.type === "flight" || segment.type === "train" || segment.type === "bus") {
        // Check if next segment is in a different country (this is a departure)
        const nextSegment = sortedSegments[index + 1];
        if (!nextSegment || nextSegment.country !== country) {
          // This is a departure from the country - use start time
          currentVisit.endDate = segment.start_datetime || segment.date;
        } else {
          // This is transport within the same country - use end time
          currentVisit.endDate = segment.end_datetime || segment.date;
        }
      } else {
        // Accommodation or activity - use end time
        currentVisit.endDate = segment.end_datetime || segment.date;
      }
    } else {
      // Starting a new visit to a different country
      if (currentVisit) {
        visits.push(currentVisit);
      }
      
      // Create new visit with traveler tracking
      const travelerIds = new Set();
      if (segment.traveler_ids) {
        segment.traveler_ids.forEach(tid => travelerIds.add(tid));
      }
      
      currentVisit = {
        country: country,
        startDate: segment.start_datetime || segment.date,
        endDate: segment.end_datetime || segment.date,
        segments: [segment],
        travelerIds: travelerIds
      };
    }
  });

  // Don't forget to add the last visit
  if (currentVisit) {
    visits.push(currentVisit);
  }

  // Calculate days for each visit
  return visits.map((visit) => {
    const start = new Date(visit.startDate);
    const end = new Date(visit.endDate);
    
    // Calculate the difference in days, rounding to handle partial days
    // Then add 1 to include both the arrival and departure day
    const millisecondsDiff = end - start;
    const daysDiff = Math.floor(millisecondsDiff / (1000 * 60 * 60 * 24));
    const days = daysDiff + 1; // +1 to include both start and end day

    return {
      country: visit.country,
      startDate: visit.startDate,
      endDate: visit.endDate,
      days: days,
      segmentCount: visit.segments.length,
      segmentIds: visit.segments.map(s => s.id),
      travelerIds: Array.from(visit.travelerIds || [])
    };
  });
};

/**
 * Get total days spent in each country across all visits
 */
export const getTotalDaysByCountry = (visits) => {
  const totals = {};
  
  visits.forEach((visit) => {
    if (totals[visit.country]) {
      totals[visit.country] += visit.days;
    } else {
      totals[visit.country] = visit.days;
    }
  });

  return Object.entries(totals).map(([country, days]) => ({
    country,
    days
  })).sort((a, b) => b.days - a.days); // Sort by days descending
};
